'use client';

import { c as _c } from "react/compiler-runtime";
import type { DeletePrivateWikiResponse, PrivateWikiSlotsResponse } from '@/lib/wiki/private-wiki-slot-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
export const privateWikiSlotsQueryKey = (installationId: number | null | undefined) => ['private-wiki-slots', installationId] as const;
type DeletePrivateWikiInput = {
  installationId: number;
  repositoryId: number;
  reservationId: string;
  latestWikiId: string | null;
};
export class DeletePrivateWikiError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
    this.name = 'DeletePrivateWikiError';
  }
}
async function readError(response: Response): Promise<DeletePrivateWikiError> {
  try {
    const payload = (await response.json()) as {
      error?: unknown;
      code?: unknown;
    };
    const message = typeof payload.error === 'string' ? payload.error : 'Failed to delete wiki';
    const code = typeof payload.code === 'string' ? payload.code : undefined;
    return new DeletePrivateWikiError(message, code);
  } catch {
    return new DeletePrivateWikiError('Failed to delete wiki');
  }
}
export function usePrivateWikiSlots(installationId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== installationId) {
    t0 = privateWikiSlotsQueryKey(installationId);
    $[0] = installationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!installationId;
  let t2;
  if ($[2] !== installationId) {
    t2 = async t3 => {
      const {
        signal
      } = t3;
      const response = await fetch(`/api/wiki/slots?installationId=${installationId}`, {
        signal
      });
      if (!response.ok) {
        throw new Error("Failed to load private wiki slots");
      }
      return response.json() as Promise<PrivateWikiSlotsResponse>;
    };
    $[2] = installationId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      enabled: t1,
      staleTime: 0,
      queryFn: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useDeletePrivateWiki() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSettled: async (_response, _error, variables) => {
        await Promise.all([queryClient.invalidateQueries({
          queryKey: privateWikiSlotsQueryKey(variables.installationId)
        }), queryClient.invalidateQueries({
          queryKey: ["wikis-by-installation", variables.installationId]
        }), queryClient.invalidateQueries({
          queryKey: ["installation-repositories", variables.installationId]
        }), queryClient.invalidateQueries({
          queryKey: ["wiki-data-by-owner-repo"]
        }), queryClient.invalidateQueries({
          queryKey: ["wiki-generation-progress"]
        }), queryClient.invalidateQueries({
          queryKey: ["wiki-settings"]
        })]);
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp(t0) {
  const {
    installationId,
    repositoryId,
    reservationId,
    latestWikiId
  } = t0;
  const response = await fetch(`/api/wiki/repositories/${installationId}/${repositoryId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      reservationId,
      latestWikiId
    })
  });
  if (!response.ok) {
    throw await readError(response);
  }
  return response.json() as Promise<DeletePrivateWikiResponse>;
}